import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AvailabilitySlot, GetAvailabilityResult } from '../../_types/availability'
import { doOpenCall } from '../../_utilities/doOpenCall'
import {
  formatLongDateInTimezone,
  formatTimeInTimezone,
} from '../../_utilities/formatDateTime'
import {
  buildTimezoneOptions,
  formatTimezoneCalloutLabel,
} from '../../_data/timezones'
import {
  buildMonthGrid,
  formatDurationLabel,
  formatMonthHeader,
  getTodayIsoInTimezone,
  getVisibleRangeIso,
  groupSlotsByDate,
} from './helpers'
import type { SlotPickerProps } from './types'
import {
  CalendarContainer,
  CalendarHeader,
  CalendarMonthLabel,
  CalendarNavButton,
  DayCell,
  DayGrid,
  DurationChip,
  DurationChipRow,
  DurationLabel,
  DurationRow,
  EmptyState,
  HostTimezoneHint,
  PickerColumn,
  PickerLoading,
  SlotButton,
  SlotList,
  SlotsSection,
  SlotsSectionHeader,
  TimezoneLabel,
  TimezoneRow,
  TimezoneSelect,
  WeekdayCell,
  WeekdayRow,
} from './styled'

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const SlotPicker = ({
  agentUserId,
  eventTypeId,
  durationOptionsMinutes,
  defaultDurationMinutes,
  slotCapacity = 1,
  hostTimezone,
  viewerTimezone,
  onChangeViewerTimezone,
  selectedSlot,
  onSelectSlot,
}: SlotPickerProps): JSX.Element => {
  const timezone = viewerTimezone
  const timezoneOptions = useMemo(
    () => buildTimezoneOptions({ hostTimezone, viewerTimezone }),
    [hostTimezone, viewerTimezone],
  )
  const hostTimezoneLabel = useMemo(
    () => formatTimezoneCalloutLabel(hostTimezone),
    [hostTimezone],
  )
  const viewerMatchesHost = viewerTimezone === hostTimezone
  const todayInTimezone = useMemo(
    () => new Date(getTodayIsoInTimezone(timezone)),
    [timezone],
  )
  const [visibleYear, setVisibleYear] = useState<number>(
    todayInTimezone.getUTCFullYear(),
  )
  const [visibleMonth, setVisibleMonth] = useState<number>(
    todayInTimezone.getUTCMonth(),
  )

  const [selectedDurationMinutes, setSelectedDurationMinutes] =
    useState<number>(defaultDurationMinutes)
  const [slots, setSlots] = useState<AvailabilitySlot[]>([])
  const [isLoadingSlots, setIsLoadingSlots] = useState<boolean>(true)
  const [selectedIsoDate, setSelectedIsoDate] = useState<string | null>(null)

  const showDurationPicker = durationOptionsMinutes.length > 1

  const loadSlots = useCallback(async (): Promise<void> => {
    setIsLoadingSlots(true)
    try {
      const range = getVisibleRangeIso(visibleYear, visibleMonth)
      const response = await doOpenCall('publiccalendar-getavailability', {
        agentUserId,
        eventTypeId,
        startDate: range.startIso,
        endDate: range.endIso,
        durationMinutes: selectedDurationMinutes,
      })
      if (response.code === 200) {
        const parsed = JSON.parse(response.data) as GetAvailabilityResult
        setSlots(parsed.slots)
      } else {
        setSlots([])
      }
    } catch {
      setSlots([])
    } finally {
      setIsLoadingSlots(false)
    }
  }, [
    agentUserId,
    eventTypeId,
    selectedDurationMinutes,
    visibleYear,
    visibleMonth,
  ])

  useEffect(() => {
    loadSlots()
  }, [loadSlots])

  const slotsByDate = useMemo(
    () => groupSlotsByDate(slots, timezone),
    [slots, timezone],
  )

  const todayIso = useMemo(
    () => getTodayIsoInTimezone(timezone),
    [timezone],
  )

  const monthGrid = useMemo(
    () =>
      buildMonthGrid(
        visibleYear,
        visibleMonth,
        new Set(Object.keys(slotsByDate)),
        todayIso,
      ),
    [visibleYear, visibleMonth, slotsByDate, todayIso],
  )

  const handlePreviousMonth = useCallback((): void => {
    if (visibleMonth === 0) {
      setVisibleMonth(11)
      setVisibleYear(previous => previous - 1)
    } else {
      setVisibleMonth(previous => previous - 1)
    }
    setSelectedIsoDate(null)
  }, [visibleMonth])

  const handleNextMonth = useCallback((): void => {
    if (visibleMonth === 11) {
      setVisibleMonth(0)
      setVisibleYear(previous => previous + 1)
    } else {
      setVisibleMonth(previous => previous + 1)
    }
    setSelectedIsoDate(null)
  }, [visibleMonth])

  const slotsForSelectedDate: AvailabilitySlot[] = selectedIsoDate
    ? slotsByDate[selectedIsoDate] ?? []
    : []

  const previousMonthIsInPast = useMemo(() => {
    const firstOfVisible = new Date(Date.UTC(visibleYear, visibleMonth, 1))
    const todayDate = new Date(`${todayIso}T00:00:00Z`)
    return firstOfVisible <= todayDate
  }, [visibleYear, visibleMonth, todayIso])

  const handleSelectDuration = useCallback((nextDuration: number): void => {
    setSelectedDurationMinutes(nextDuration)
    setSelectedIsoDate(null)
  }, [])

  return (
    <PickerColumn>
      {showDurationPicker ? (
        <DurationRow>
          <DurationLabel>Duration</DurationLabel>
          <DurationChipRow>
            {durationOptionsMinutes.map(option => (
              <DurationChip
                key={option}
                type="button"
                isSelected={option === selectedDurationMinutes}
                onClick={() => handleSelectDuration(option)}
              >
                {formatDurationLabel(option)}
              </DurationChip>
            ))}
          </DurationChipRow>
        </DurationRow>
      ) : null}

      <TimezoneRow>
        <TimezoneLabel htmlFor="slot-picker-timezone">
          Showing times in
        </TimezoneLabel>
        <TimezoneSelect
          id="slot-picker-timezone"
          value={timezone}
          onChange={event => onChangeViewerTimezone(event.target.value)}
        >
          {timezoneOptions.map(option => (
            <option key={option.id} value={option.id}>
              {option.displayValue}
            </option>
          ))}
        </TimezoneSelect>
        {!viewerMatchesHost && hostTimezoneLabel.length > 0 ? (
          <HostTimezoneHint>
            Host&apos;s local time: {hostTimezoneLabel}
          </HostTimezoneHint>
        ) : null}
      </TimezoneRow>

      <CalendarContainer>
        <CalendarHeader>
          <CalendarNavButton
            type="button"
            onClick={handlePreviousMonth}
            disabled={previousMonthIsInPast}
            aria-label="Previous month"
          >
            ‹
          </CalendarNavButton>
          <CalendarMonthLabel>
            {formatMonthHeader(visibleYear, visibleMonth)}
          </CalendarMonthLabel>
          <CalendarNavButton
            type="button"
            onClick={handleNextMonth}
            aria-label="Next month"
          >
            ›
          </CalendarNavButton>
        </CalendarHeader>

        <WeekdayRow>
          {WEEKDAY_LABELS.map(label => (
            <WeekdayCell key={label}>{label}</WeekdayCell>
          ))}
        </WeekdayRow>

        <DayGrid>
          {monthGrid.map(cell => {
            if (cell.isOutsideMonth) {
              return (
                <DayCell
                  key={cell.isoDate}
                  type="button"
                  state="outsideMonth"
                  disabled
                >
                  {cell.dayOfMonth}
                </DayCell>
              )
            }
            if (cell.isoDate < todayIso) {
              return (
                <DayCell
                  key={cell.isoDate}
                  type="button"
                  state="disabled"
                  isToday={cell.isToday}
                  disabled
                >
                  {cell.dayOfMonth}
                </DayCell>
              )
            }
            if (!cell.hasSlots) {
              return (
                <DayCell
                  key={cell.isoDate}
                  type="button"
                  state="disabled"
                  isToday={cell.isToday}
                  disabled
                >
                  {cell.dayOfMonth}
                </DayCell>
              )
            }
            const isSelected = cell.isoDate === selectedIsoDate
            return (
              <DayCell
                key={cell.isoDate}
                type="button"
                state={isSelected ? 'selected' : 'available'}
                isToday={cell.isToday}
                onClick={() => setSelectedIsoDate(cell.isoDate)}
              >
                {cell.dayOfMonth}
              </DayCell>
            )
          })}
        </DayGrid>
      </CalendarContainer>

      {isLoadingSlots ? (
        <PickerLoading>Loading availability…</PickerLoading>
      ) : selectedIsoDate ? (
        <SlotsSection>
          <SlotsSectionHeader>
            {formatLongDateInTimezone(
              slotsForSelectedDate[0]?.startTime ?? Date.now(),
              timezone,
            )}
          </SlotsSectionHeader>
          {slotsForSelectedDate.length === 0 ? (
            <EmptyState>No slots available on this date.</EmptyState>
          ) : (
            <SlotList>
              {slotsForSelectedDate.map(slot => (
                <SlotButton
                  key={slot.startTime}
                  type="button"
                  isSelected={
                    selectedSlot !== null &&
                    selectedSlot.startTime === slot.startTime
                  }
                  onClick={() => onSelectSlot(slot, selectedDurationMinutes)}
                >
                  {formatTimeInTimezone(slot.startTime, timezone)}
                  {slotCapacity > 1 &&
                  typeof slot.remainingCapacity === 'number'
                    ? ` · ${slot.remainingCapacity} left`
                    : ''}
                </SlotButton>
              ))}
            </SlotList>
          )}
        </SlotsSection>
      ) : (
        <EmptyState>
          {Object.keys(slotsByDate).length === 0
            ? 'No available times this month. Try a later month.'
            : 'Pick a date to see available times.'}
        </EmptyState>
      )}
    </PickerColumn>
  )
}

export default SlotPicker
